import type { DocumentDTO } from "@zplanem/responses"
import { useEffect } from "react"
import { Button } from "@/components/ui/button"
import { useDocumentAiReview } from "../../hooks/use-documents"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"

type Finding = {
	quote?: string
	explanation?: string
	severity?: string
}

type ReviewResponse = {
	summary?: string
	criticalIssues?: Finding[]
	advantages?: Finding[]
	disadvantages?: Finding[]
	missingPoints?: Finding[]
	possibleIssues?: Finding[]
	abusiveClauses?: Finding[]
}

/**
 * Read-only view of Mareczek's AI review for a document (ZPL-209) —
 * contract (ZPL-263) or land report (ZPL-331); both share this component.
 * Modeled on the document edit modal (backdrop + Escape + close ×), but wider
 * and scrollable since a review can be long. The data comes from
 * `useDocumentAiReview`; realtime freshness is handled by the list-level
 * `useDocumentReviewRealtime`. Severity is free-form, localized text from the
 * model — we tint it heuristically and otherwise render it verbatim.
 */
export function DocumentAiReviewModal({
	propertyId,
	document,
	onClose,
}: {
	propertyId: string
	document: DocumentDTO
	onClose: () => void
}) {
	const { locale } = useLocale()
	const td = getTranslations(locale).projects.documents
	// Only fetch when the viewer can download the document — the review follows
	// the same CASL "read" tier as download (defense-in-depth; the badge that
	// opens this modal is already canDownload-gated, and the server enforces it).
	const { data: review, isLoading } = useDocumentAiReview(
		propertyId,
		document.id,
		document.canDownload,
	)

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose()
		}
		window.addEventListener("keydown", handler)
		return () => window.removeEventListener("keydown", handler)
	}, [onClose])

	// Unreachable in practice (the badge is canDownload-gated), but guard anyway
	// so a direct caller can't open a review for a non-downloadable document.
	if (!document.canDownload) return null

	const processed = review?.processedAt != null && review.failedAt == null
	const failed = review?.failedAt != null
	const response = processed
		? ((review?.response ?? {}) as ReviewResponse)
		: null

	const sections: { heading: string; findings: Finding[] }[] = response
		? [
				{
					heading: td.aiReviewCritical,
					findings: response.criticalIssues ?? [],
				},
				{
					heading: td.aiReviewAbusive,
					findings: response.abusiveClauses ?? [],
				},
				{
					heading: td.aiReviewDisadvantages,
					findings: response.disadvantages ?? [],
				},
				{ heading: td.aiReviewMissing, findings: response.missingPoints ?? [] },
				{
					heading: td.aiReviewPossible,
					findings: response.possibleIssues ?? [],
				},
				{ heading: td.aiReviewAdvantages, findings: response.advantages ?? [] },
			].filter((s) => s.findings.length > 0)
		: []

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-labelledby="doc-ai-review-title"
			className="fixed inset-0 z-50 flex items-center justify-center px-4"
		>
			<button
				type="button"
				aria-label={td.close}
				className="absolute inset-0 bg-black/60"
				onClick={onClose}
			/>
			<div className="relative flex max-h-[85vh] w-full max-w-2xl flex-col rounded-lg border border-border bg-popover text-popover-foreground shadow-2xl">
				<div className="flex items-start justify-between gap-3 border-b border-border p-6 pb-4">
					<div className="min-w-0">
						<h2
							id="doc-ai-review-title"
							className="text-xl font-bold text-foreground"
						>
							{td.aiReviewModalTitle}
						</h2>
						<p className="mt-0.5 truncate text-sm text-muted-foreground">
							{document.title}
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						aria-label={td.close}
						className="shrink-0 px-2 py-1 text-muted-foreground hover:text-foreground"
					>
						×
					</button>
				</div>

				<div className="min-h-0 flex-1 overflow-y-auto p-6 pt-4">
					{!review && isLoading ? (
						<ProcessingBlock label={td.aiReviewProcessing} />
					) : null}

					{review && !processed && !failed ? (
						<ProcessingBlock label={td.aiReviewProcessing} />
					) : null}

					{failed ? (
						<div
							role="alert"
							className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
						>
							{failureMessage(review?.failureReason, td)}
						</div>
					) : null}

					{processed ? (
						<div className="space-y-5">
							{response?.summary ? (
								<section>
									<h3 className="mb-1 text-sm font-semibold text-foreground">
										{td.aiReviewSummary}
									</h3>
									<p className="whitespace-pre-wrap text-sm text-foreground/90">
										{response.summary}
									</p>
								</section>
							) : null}

							{sections.map((section) => (
								<Section
									key={section.heading}
									heading={section.heading}
									findings={section.findings}
								/>
							))}

							{!response?.summary && sections.length === 0 ? (
								<p className="text-sm text-muted-foreground">
									{td.aiReviewNoFindings}
								</p>
							) : null}

							<p className="border-t border-border pt-4 text-xs text-muted-foreground">
								{td.aiReviewDisclaimer}
							</p>
						</div>
					) : null}
				</div>

				<div className="flex justify-end border-t border-border p-4">
					<Button variant="ghost" onClick={onClose}>
						{td.close}
					</Button>
				</div>
			</div>
		</div>
	)
}

function ProcessingBlock({ label }: { label: string }) {
	return (
		<div className="flex items-center gap-2 text-sm text-muted-foreground">
			<span className="h-2 w-2 animate-pulse rounded-full bg-warning" />
			{label}…
		</div>
	)
}

function Section({
	heading,
	findings,
}: {
	heading: string
	findings: Finding[]
}) {
	return (
		<section>
			<h3 className="mb-2 text-sm font-semibold text-foreground">{heading}</h3>
			<ul className="space-y-2">
				{findings.map((finding) => (
					<li
						// No stable id from the model; key on content (the list is
						// read-only and never reorders).
						key={`${heading}:${finding.quote ?? ""}:${finding.explanation ?? ""}`}
						className="rounded-xl border border-border bg-muted/30 p-3"
					>
						<div className="flex items-start justify-between gap-2">
							{finding.explanation ? (
								<p className="text-sm text-foreground/90">
									{finding.explanation}
								</p>
							) : (
								<span />
							)}
							{finding.severity ? (
								<span
									className={`shrink-0 rounded-sm border px-1.5 py-0.5 text-[10px] font-medium uppercase leading-none ${severityTone(
										finding.severity,
									)}`}
								>
									{finding.severity}
								</span>
							) : null}
						</div>
						{finding.quote ? (
							<blockquote className="mt-2 border-l-2 border-border pl-3 text-xs italic text-muted-foreground">
								"{finding.quote}"
							</blockquote>
						) : null}
					</li>
				))}
			</ul>
		</section>
	)
}

/** Map a stored failure code to friendly, localized copy. */
function failureMessage(
	reason: string | null | undefined,
	td: ReturnType<typeof getTranslations>["projects"]["documents"],
): string {
	if (reason === "DOCUMENT_NOT_A_CONTRACT") return td.aiReviewFailedNotContract
	if (reason === "DOCUMENT_NOT_A_LAND_REPORT")
		return td.aiReviewFailedNotLandReport
	if (reason === "DOCUMENT_UNREADABLE") return td.aiReviewFailedUnreadable
	return td.aiReviewFailed
}

/** Heuristic tint for the model's free-form, localized severity label. */
function severityTone(severity: string): string {
	const s = severity.toLowerCase()
	if (/(crit|high|wysok|krytycz|критич|висок|серйоз)/.test(s)) {
		return "border-destructive/30 bg-destructive/10 text-destructive"
	}
	if (/(med|mod|śred|sredn|середн|помірн)/.test(s)) {
		return "border-warning/40 bg-warning-muted text-warning-muted-foreground"
	}
	if (/(low|nisk|низьк|незнач)/.test(s)) {
		return "border-success/40 bg-success-muted text-success-muted-foreground"
	}
	return "border-border bg-muted text-muted-foreground"
}
